/**
 * excelHeaders — Excel export 헤더 공용 빌더.
 *
 * 【역할】
 * - Excel export 유틸 (useExcel.exportToExcel) 이 요구하는 `{key, header, width}` 배열을
 *   i18n 라벨 키 기반으로 생성. 페이지별 하드코드 한글 헤더 제거.
 *
 * 【주요 기능】
 * - buildExcelHeaders(specs, t, defaultWidth?): i18n 번역 + 기본 width 적용
 *
 * 【사용 예시】
 * ```tsx
 * const { t } = useLabel()
 * const headers = buildExcelHeaders([
 *   { key: 'productId', labelKey: 'COL_PRODUCT_ID', fallback: '상품ID', width: 12 },
 *   { key: 'productName', labelKey: 'COL_PRODUCT_NAME', fallback: '상품명', width: 25 },
 * ], t)
 * exportToExcel(rows, headers, t('FILE_PRODUCT_LIST', 'products.xlsx'))
 * ```
 *
 * 【왜 이 패턴?】
 * - Excel export 가 있는 모든 페이지에서 동일한 헤더 선언 구조를 사용하도록 강제 → i18n 일관성.
 */
import type { TranslateFn } from './makeColumns'

/** ExcelHeaderSpec — 엑셀 헤더 1 컬럼 선언 DTO. */
export interface ExcelHeaderSpec {
  /** 데이터 객체의 키 */
  key: string
  /** i18n 라벨 키 (보통 COL_*) */
  labelKey: string
  /** 번역 누락 시 표시할 기본 헤더 */
  fallback?: string
  /** 엑셀 열 너비 (문자 수 기준, 기본 15) */
  width?: number
}

/**
 * ExcelHeaderSpec 배열을 useExcel.exportToExcel 이 소비 가능한 shape 으로 변환.
 * @param specs - 헤더 선언 배열.
 * @param t - 번역 함수.
 * @param defaultWidth - 개별 width 미지정 시 사용할 기본값 (기본 15).
 * @returns `{ key, header, width }[]` — ExcelColumn 호환.
 */
export function buildExcelHeaders(
  specs: ReadonlyArray<ExcelHeaderSpec>,
  t: TranslateFn,
  defaultWidth = 15
): { key: string; header: string; width: number }[] {
  return specs.map((s) => ({
    key: s.key,
    header: t(s.labelKey, s.fallback ?? s.labelKey),
    width: s.width ?? defaultWidth
  }))
}
